import React from 'react';
import { Route, useParams } from 'react-router-dom';

// Shared order component
const AllOrders = React.lazy(() => import('../components/orders/AllOrders').then(m => ({ default: m.AllOrders })));

// Fleet run folder — reads the route id from the URL
const FleetRunOrders = () => {
  const { routeId } = useParams<{ routeId: string }>();
  return <AllOrders courier="sydney-fleet" routeId={routeId ? Number(routeId) : undefined} />;
};

// Legacy order pages (for non-standard routes)
const OrdersPage = React.lazy(() => import('../pages/OrdersPage').then(m => ({ default: m.OrdersPage })));
const OrderDetailPage = React.lazy(() => import('../pages/OrderDetailPage'));
const SingleOrderTest = React.lazy(() => import('../pages/SingleOrderTest').then(m => ({ default: m.SingleOrderTest })));
const CancelledOrders = React.lazy(() => import('../pages/orderStatus/CancelledOrders').then(m => ({ default: m.CancelledOrders })));
const ReturnsOrders = React.lazy(() => import('../pages/orderStatus/ReturnsOrders').then(m => ({ default: m.ReturnsOrders })));

export const orderRoutes = [
  // ============================================================================
  // QUOTE
  // ============================================================================
  <Route 
    key="/orders/quote" 
    path="/orders/quote" 
    element={<AllOrders status="quote" title="Quote Orders" />} 
  />,
  
  // ============================================================================
  // BACKORDER
  // ============================================================================
  <Route 
    key="/orders/backorder" 
    path="/orders/backorder" 
    element={<AllOrders status="backorder" title="Backorder Orders" />} 
  />,
  
  // ============================================================================
  // MAIN ORDERS PAGE
  // ============================================================================
  <Route key="/orders" path="/orders" element={<OrdersPage />} />,
  <Route key="/orders/test" path="/orders/test" element={<SingleOrderTest />} />,
  
  // ============================================================================
  // SYDNEY FLEET - All orders allocated to Sydney Fleet
  // ============================================================================
  // Main folder: All Sydney Fleet orders (any status)
  <Route 
    key="/orders/sydney-fleet" 
    path="/orders/sydney-fleet" 
    element={<AllOrders courier="sydney-fleet" />} 
  />,
  
  // Subfolders: Sydney Fleet + Status
  <Route 
    key="/orders/sydney-fleet/pending" 
    path="/orders/sydney-fleet/pending" 
    element={<AllOrders courier="sydney-fleet" status="pending" />} 
  />,
  <Route 
    key="/orders/sydney-fleet/dispatched" 
    path="/orders/sydney-fleet/dispatched" 
    element={<AllOrders courier="sydney-fleet" status="shipped,out_for_delivery,delivered" />} 
  />,
  
  // Fleet run folders: orders attached to a specific route
  <Route 
    key="/orders/sydney-fleet/run/:routeId" 
    path="/orders/sydney-fleet/run/:routeId" 
    element={<FleetRunOrders />} 
  />,
  
  // ============================================================================
  // SYDNEY FLEET BULK
  // ============================================================================
  <Route 
    key="/orders/sydney-fleet-bulk" 
    path="/orders/sydney-fleet-bulk" 
    element={<AllOrders courier="sydney-fleet-bulk" />} 
  />,
  <Route 
    key="/orders/sydney-fleet-bulk/pending" 
    path="/orders/sydney-fleet-bulk/pending" 
    element={<AllOrders courier="sydney-fleet-bulk" status="pending" />} 
  />,
  <Route 
    key="/orders/sydney-fleet-bulk/dispatched" 
    path="/orders/sydney-fleet-bulk/dispatched" 
    element={<AllOrders courier="sydney-fleet-bulk" status="shipped,out_for_delivery,delivered" />} 
  />,
  
  // ============================================================================
  // BORDER EXPRESS
  // ============================================================================
  <Route 
    key="/orders/border-express" 
    path="/orders/border-express" 
    element={<AllOrders courier="border-express" />} 
  />,
  <Route 
    key="/orders/border-express/pending" 
    path="/orders/border-express/pending" 
    element={<AllOrders courier="border-express" status="pending" />} 
  />,
  <Route 
    key="/orders/border-express/dispatched" 
    path="/orders/border-express/dispatched" 
    element={<AllOrders courier="border-express" status="shipped" />} 
  />,
  
  // ============================================================================
  // BORDER EXPRESS BULK
  // ============================================================================
  <Route 
    key="/orders/border-express-bulk" 
    path="/orders/border-express-bulk" 
    element={<AllOrders courier="border-express-bulk" />} 
  />,
  <Route 
    key="/orders/border-express-bulk/pending" 
    path="/orders/border-express-bulk/pending" 
    element={<AllOrders courier="border-express-bulk" status="pending" />} 
  />,
  <Route 
    key="/orders/border-express-bulk/dispatched" 
    path="/orders/border-express-bulk/dispatched" 
    element={<AllOrders courier="border-express-bulk" status="shipped" />} 
  />,
  
  // ============================================================================
  // COURIER PLEASE
  // ============================================================================
  <Route 
    key="/orders/courier-please" 
    path="/orders/courier-please" 
    element={<AllOrders courier="courier-please" />} 
  />,
  <Route 
    key="/orders/courier-please/pending" 
    path="/orders/courier-please/pending" 
    element={<AllOrders courier="courier-please" status="pending" />} 
  />,
  <Route 
    key="/orders/courier-please/dispatched" 
    path="/orders/courier-please/dispatched" 
    element={<AllOrders courier="courier-please" status="shipped" />} 
  />,
  
  // ============================================================================
  // VELLEX
  // ============================================================================
  <Route
    key="/orders/vellex"
    path="/orders/vellex"
    element={<AllOrders courier="vellex" />}
  />,
  <Route
    key="/orders/vellex/pending"
    path="/orders/vellex/pending"
    element={<AllOrders courier="vellex" status="pending" />}
  />,
  <Route
    key="/orders/vellex/dispatched"
    path="/orders/vellex/dispatched"
    element={<AllOrders courier="vellex" status="shipped" />}
  />,

  // ============================================================================
  // MAINFREIGHT
  // ============================================================================
  <Route
    key="/orders/mainfreight"
    path="/orders/mainfreight"
    element={<AllOrders courier="mainfreight" />}
  />,
  <Route
    key="/orders/mainfreight/pending"
    path="/orders/mainfreight/pending"
    element={<AllOrders courier="mainfreight" status="pending" />}
  />,
  <Route
    key="/orders/mainfreight/dispatched"
    path="/orders/mainfreight/dispatched"
    element={<AllOrders courier="mainfreight" status="shipped" />}
  />,

  // ============================================================================
  // PICK UP IN STORE - By shipping method (no courier assignment needed)
  // ============================================================================
  <Route 
    key="/orders/pickup-instore" 
    path="/orders/pickup-instore" 
    element={<AllOrders shippingMethod="Pickup%" />} 
  />,
  <Route 
    key="/orders/pickup-instore/pending" 
    path="/orders/pickup-instore/pending" 
    element={<AllOrders shippingMethod="Pickup%" status="pending" />} 
  />,
  <Route 
    key="/orders/pickup-instore/dispatched" 
    path="/orders/pickup-instore/dispatched" 
    element={<AllOrders shippingMethod="Pickup%" status="shipped" />} 
  />,
  <Route 
    key="/orders/pickup-instore/ready" 
    path="/orders/pickup-instore/ready" 
    element={<AllOrders shippingMethod="Pickup%" status="ready_to_pickup" />} 
  />,
  
  // ============================================================================
  // ON HOLD - Main folder: On hold with NO reason assigned
  // ============================================================================
  <Route 
    key="/orders/onhold" 
    path="/orders/onhold" 
    element={<AllOrders status="on_hold" holdReason="" />} 
  />,
  
  // On Hold Subfolders: On hold + specific reason
  <Route 
    key="/orders/onhold/unallocated" 
    path="/orders/onhold/unallocated" 
    element={<AllOrders status="on_hold" holdReason="" />} 
  />,
  <Route 
    key="/orders/onhold/awaiting-payment" 
    path="/orders/onhold/awaiting-payment" 
    element={<AllOrders status="on_hold" holdReason="awaiting-payment" />} 
  />,
  <Route 
    key="/orders/onhold/awaiting-stock" 
    path="/orders/onhold/awaiting-stock" 
    element={<AllOrders status="on_hold" holdReason="awaiting-stock" />} 
  />,
  <Route 
    key="/orders/onhold/customer-issue" 
    path="/orders/onhold/customer-issue" 
    element={<AllOrders status="on_hold" holdReason="customer-issue" />} 
  />,
  <Route 
    key="/orders/onhold/freight-issue" 
    path="/orders/onhold/freight-issue" 
    element={<AllOrders status="on_hold" holdReason="freight-issue" />} 
  />,
  <Route 
    key="/orders/onhold/internal-issue" 
    path="/orders/onhold/internal-issue" 
    element={<AllOrders status="on_hold" holdReason="internal-issue" />} 
  />,
  
  // ============================================================================
  // CANCELLED
  // ============================================================================
  <Route 
    key="/orders/cancelled" 
    path="/orders/cancelled" 
    element={<AllOrders status="cancelled" />} 
  />,
  
  // ============================================================================
  // RETURNS
  // ============================================================================
  <Route 
    key="/orders/returns" 
    path="/orders/returns" 
    element={<ReturnsOrders />} 
  />,
  <Route 
    key="/orders/returns/new" 
    path="/orders/returns/new" 
    element={<ReturnsOrders status="new" />} 
  />,
  <Route 
    key="/orders/returns/processing" 
    path="/orders/returns/processing" 
    element={<ReturnsOrders status="processing" />} 
  />,
  <Route 
    key="/orders/returns/completed" 
    path="/orders/returns/completed" 
    element={<ReturnsOrders status="completed" />} 
  />,
  
  // Order detail - MUST be last to avoid matching more specific routes
  <Route key="/orders/:orderId" path="/orders/:orderId" element={<OrderDetailPage />} />,
];
